
/**
 * File Name          : idl-physens.service.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.0.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_BACKGROUNDTECH_LICENSE.txt', which is part of this source code package.
 */

import { Injectable, Output } from '@angular/core';
import { BleClient, BleDevice, ScanResult } from '@capacitor-community/bluetooth-le';
import { Subject, BehaviorSubject } from 'rxjs';
import { CommandPacket, PACKET_SUB_TYPE, Packet, PACKET_TYPE, ResponsePacket, Publisher, AppCommandPacket, AppResponsePacket, Parameter, AppStatusPacket, ParameterSet, RESPONSE_RESULT, ACCESS_CONTROL_ROLE, FILERECORDER_STATE } from './models';
import { OUTPUTSET_TYPE, OutputsetPacket } from './models/packets/outputset-packet.model';
import { McumgrCap } from './utilities/mcumgr-cap';
import { Drive_Item, DRIVE_ITEM_TYPE } from './models/file_system/drive-item.model';
import { Capacitor } from '@capacitor/core';
import { BLE_CHARGE_ICON, bleChargeIconToString } from './models/battery_state_char.model';
import { DeviceFileSubscription } from './models/subscription/device-file-sub.model';
import { RESULT_CODE } from './models/packets/response-result.model';
import { GenericPacket } from './models/packets/generic-packet.model';
import { PhysensBleDevice } from './models/physens-devices/physens-ble-device.model';
import { OTA_DIAG } from './models/physens-devices/physens-device.model';
import { HexStringUtils } from './utilities/hex-string-utils';
import { GATT_UUID } from './models/uuid/gatt-uuid.model';

// Re-export types for backward compatibility
export { CommunicationType, AppResponsePacketPromise, PacketResponsePromise } from './models/communication-types';

@Injectable({
  providedIn: 'root'
})
export class IdlPhysensService {
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private requestDevicePromise: Promise<BleDevice> | null = null;
  private scanning = false;
  private scanningSubject = new Subject<ScanResult>();

  private devices = new Map<string, PhysensBleDevice>();
  private devicesSubject = new Subject<PhysensBleDevice[]>();
  
  newPacketSubject = new Subject<{deviceId:string, pkt:Packet|ResponsePacket|OutputsetPacket}>()

  constructor() {


  }

  static stringToHex(str: string): string {
    return HexStringUtils.stringToHex(str);
  }

  static hexToString(hex: string): string {
    return HexStringUtils.hexToString(hex);
  }

  async initializeBLE() {
    if (this.initialized) {
      return;
    }

    if (this.initializationPromise) {
      await this.initializationPromise;
      return;
    }

    this.initializationPromise = BleClient.initialize()
      .then(() => {
        this.initialized = true;
      })
      .finally(() => {
        this.initializationPromise = null;
      });

    await this.initializationPromise;
  }

  async forceInitializeBLE() {
    this.initialized = false;
    this.initializationPromise = null;
    await this.initializeBLE();
  }

  async scanForDevice(namePrefix: string, exactName = false): Promise<PhysensBleDevice> {
    const platform = Capacitor.getPlatform();
    if (platform !== 'web') {
      await this.initializeBLE();
    }

    const bleDevice = await this.requestDeviceWithGuard(namePrefix, exactName);

    if (!this.initialized) {
      await this.initializeBLE();
    }

    console.log("device id: " + bleDevice.deviceId)

    const deviceId = IdlPhysensService.stringToHex(bleDevice.deviceId) 

    var device = new PhysensBleDevice(deviceId, bleDevice.name)
    device.packet_subject.subscribe((value)=>{
      this.newPacket(deviceId, value)
    })

    this.addDevice(device)

    return device
    // return this.scanningSubject
  }

  async scanForDeviceWithoutInstance(namePrefix: string): Promise<string> {
    const platform = Capacitor.getPlatform();
    if (platform !== 'web') {
      await this.initializeBLE();
    }

    const bleDevice = await this.requestDeviceWithGuard(namePrefix);

    if (!this.initialized) {
      await this.initializeBLE();
    }

    console.log('reselect chooser returned device id: ' + bleDevice.deviceId)
    return IdlPhysensService.stringToHex(bleDevice.deviceId)
  }

  scanResultsSubject = new BehaviorSubject<ScanResult[]>([])

  async startDeviceScan(): Promise<BehaviorSubject<ScanResult[]>> {
    await this.initializeBLE()
    if (Capacitor.getPlatform() === 'android') {
      BleClient.requestLEScan({ manufacturerData: [{companyIdentifier: 0x0A5C, dataPrefix: new Uint8Array([])}] }, (result: ScanResult)=>{
        console.log("Found result: " + result.localName)
        const currentResults = this.scanResultsSubject.value || [];
        const updatedResults = [...currentResults, result];
        this.scanResultsSubject.next(updatedResults);
      })
    }

    return this.scanResultsSubject
  }

  stopDeviceScan() {
    BleClient.stopLEScan();
    this.scanResultsSubject.next([]);

  }

  addNonScannedDevice(device: PhysensBleDevice) {
    device.packet_subject.subscribe((value)=>{
      this.newPacket(device.deviceId, value)
    })

    this.addDevice(device)
  }

  addScannedDevice(deviceIdInput: string, deviceName: string): PhysensBleDevice {
    const deviceId = IdlPhysensService.stringToHex(deviceIdInput) 

    var device = new PhysensBleDevice(deviceId, deviceName)
    device.packet_subject.subscribe((value)=>{
      this.newPacket(deviceId, value)
    })

    this.addDevice(device)

    return device
  }

  private LEScanCallback(result: ScanResult) {
    this.scanningSubject.next(result)
  }

  private addDevice(device: PhysensBleDevice) {
    // scanForDevice() always constructs a brand-new PhysensBleDevice, even when the
    // chooser resolves to a deviceId we already have an instance for (e.g. the
    // Reconnect-button reselect path). Without disposing the stale instance here, it
    // keeps running its own reconnect timers/listeners in the background after being
    // silently evicted from this map — nothing else ever calls .remove() on it, so a
    // later manual disconnect (which only acts on whatever instance is in this map)
    // leaves it free to auto-reconnect behind the user's back.
    const existing = this.devices.get(device.deviceId)
    if (existing && existing !== device) {
      console.log(`[BLE][${device.deviceName ?? device.deviceId}] disposing stale device instance before registering new one`)
      existing.remove()
    }

    this.devices.set(device.deviceId, device)

    this.devicesSubject.next(Array.from(this.devices.values()))
  }

  removeDevice(deviceId: string) {
    this.devices.get(deviceId)?.remove()
    BleClient.disconnect(IdlPhysensService.hexToString(deviceId)).then(()=>{
      this.devices.delete(deviceId)

      this.devicesSubject.next(Array.from(this.devices.values()))
    })
    
  }

  async removeAll() {
    for (const device of this.devices.values()) {
      device.remove();
      await BleClient.disconnect(IdlPhysensService.hexToString(device.deviceId));
    }
  }

  getDevices(): [Subject<PhysensBleDevice[]>, PhysensBleDevice[]] {
    return [this.devicesSubject, Array.from(this.devices.values())]
  }
  
  getDevice(deviceID: string): PhysensBleDevice | undefined {
    return this.devices.get(deviceID)
  }

  private newPacket(deviceId:string, pkt: Packet|ResponsePacket|OutputsetPacket) {
    this.newPacketSubject.next({deviceId, pkt})
  }

  /**
   * Clears the in-flight chooser promise reference so the next scanForDevice()/
   * requestDeviceWithGuard() call starts a brand-new Bluetooth chooser request
   * instead of reusing a dead/expired one (e.g. after a caller-side timeout).
   */
  cancelPendingDeviceRequest(): void {
    this.requestDevicePromise = null;
  }

  private requestDeviceWithGuard(namePrefix: string, exactName = false): Promise<BleDevice> {
    if (this.requestDevicePromise) {
      console.warn('Bluetooth chooser request already in progress; reusing existing chooser promise.');
      return this.requestDevicePromise;
    }

    // --- Fix 2 investigation (log only, no behavior change) ---
    // A chooser only paints if requestDevice() runs while the user activation from the click
    // is still live. isActive===false here means an await between the click and this call
    // consumed the gesture, so Chrome will silently block the chooser.
    const ua = (typeof navigator !== 'undefined') ? (navigator as any).userActivation : undefined;
    if (OTA_DIAG) console.log(`[FWUpdate][DIAG] requestDevice() about to be called in this task; navigator.userActivation.isActive=${ua?.isActive} hasBeenActive=${ua?.hasBeenActive}`);
    const requestStartedAt = Date.now();
    let requestSettled = false;
    const pendingTimer = setTimeout(() => {
      if(!requestSettled && OTA_DIAG) console.warn('[FWUpdate][DIAG] requestDevice() STILL PENDING after 5000ms (chooser likely never painted)');
    }, 5000);

    this.requestDevicePromise = BleClient.requestDevice({
      ...(exactName ? { name: namePrefix } : { namePrefix: namePrefix }),
      optionalServices: [GATT_UUID.nusServiceGUID(0x00), McumgrCap.SERVICE_UUID]
    })
      .then((dev) => {
        requestSettled = true;
        clearTimeout(pendingTimer);
        if (OTA_DIAG) console.log(`[FWUpdate][DIAG] requestDevice() resolved after ${Date.now() - requestStartedAt}ms (device=${(dev as any)?.name ?? '?'}/${(dev as any)?.deviceId ?? '?'})`);
        return dev;
      })
      .catch((error) => {
        requestSettled = true;
        clearTimeout(pendingTimer);
        if (OTA_DIAG) console.warn(`[FWUpdate][DIAG] requestDevice() rejected after ${Date.now() - requestStartedAt}ms name=${(error as any)?.name ?? 'UnknownError'}`);
        this.logRequestDeviceError(error);
        throw error;
      })
      .finally(() => {
        this.requestDevicePromise = null;
      });

    return this.requestDevicePromise;
  }

  private logRequestDeviceError(error: unknown) {
    const err = error as { name?: string; message?: string };
    const name = err?.name ?? 'UnknownError';
    const message = err?.message ?? String(error);

    console.error('Error during device scan:', { name, message, error });

    if (name === 'NotAllowedError') {
      console.warn('Bluetooth chooser was blocked or dismissed. Ensure requestDevice is called from a direct user gesture (click/tap).');
    }

    if (name === 'SecurityError') {
      console.warn('Web Bluetooth requires HTTPS or localhost and a browser with bluetooth permissions enabled.');
    }
  }
}